// Pure-JS ATS scoring. Runs instantly with no AI key required, so we
// can show a live score in the editor at all times. If a job description
// is provided, keyword overlap is layered in.

const STRONG_VERBS = new Set([
    'accelerated', 'achieved', 'adapted', 'administered', 'advised', 'analyzed',
    'architected', 'authored', 'automated', 'boosted', 'built', 'championed',
    'collaborated', 'configured', 'consolidated', 'constructed', 'coordinated',
    'created', 'decreased', 'delivered', 'designed', 'developed', 'directed',
    'drove', 'engineered', 'enhanced', 'established', 'evaluated', 'executed',
    'expanded', 'facilitated', 'formulated', 'founded', 'generated', 'grew',
    'guided', 'headed', 'identified', 'implemented', 'improved', 'increased',
    'influenced', 'initiated', 'innovated', 'instituted', 'integrated', 'launched',
    'led', 'leveraged', 'managed', 'mentored', 'migrated', 'modernized',
    'monitored', 'negotiated', 'optimized', 'orchestrated', 'overhauled',
    'oversaw', 'pioneered', 'planned', 'prioritized', 'produced', 'programmed',
    'rearchitected', 'rebuilt', 'reduced', 'refactored', 'reengineered',
    'researched', 'resolved', 'restructured', 'reviewed', 'revamped', 'saved',
    'scaled', 'shipped', 'simplified', 'solved', 'spearheaded', 'standardized',
    'streamlined', 'strengthened', 'supervised', 'tested', 'trained',
    'transformed', 'unified', 'unlocked', 'upgraded', 'validated'
]);

const STOP_WORDS = new Set([
    'a', 'an', 'the', 'and', 'or', 'but', 'is', 'are', 'was', 'were', 'be',
    'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would',
    'should', 'could', 'may', 'might', 'must', 'can', 'this', 'that', 'these',
    'those', 'i', 'me', 'my', 'we', 'our', 'you', 'your', 'he', 'she', 'it',
    'they', 'them', 'their', 'as', 'of', 'in', 'on', 'at', 'to', 'for', 'with',
    'by', 'from', 'about', 'into', 'through', 'during', 'before', 'after',
    'above', 'below', 'up', 'down', 'out', 'off', 'over', 'under', 'again',
    'then', 'once', 'all', 'any', 'both', 'each', 'few', 'more', 'most',
    'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own', 'same', 'so',
    'than', 'too', 'very', 'just', 'also', 'including', 'etc'
]);

const extractKeywords = (text) => {
    if (!text) return new Set();
    return new Set(
        text.toLowerCase()
            .replace(/[^\w\s+#./-]/g, ' ')
            .split(/\s+/)
            .map(w => w.trim())
            .filter(w => w.length >= 3 && !STOP_WORDS.has(w))
    );
};

const splitBullets = (description) => {
    if (!description) return [];
    return description
        .split(/\r?\n/)
        .map(l => l.replace(/^[\s•\-*·]+/, '').trim())
        .filter(Boolean);
};

const startsWithStrongVerb = (bullet) => {
    if (!bullet) return false;
    const firstWord = bullet.split(/\s+/)[0]?.toLowerCase().replace(/[^a-z]/g, '');
    return STRONG_VERBS.has(firstWord);
};

const containsMetric = (bullet) => {
    // Numbers, percentages, dollar amounts, time durations
    return /\b(\d+%|\$\d+|\d+[kmKM]\b|\d{2,}|\d+x\b)/i.test(bullet);
};

export const scoreResume = (resumeData, jobDescription = '') => {
    const checks = [];
    const p = resumeData.personal || {};
    const allBullets = (resumeData.experience || []).flatMap(e => splitBullets(e.description));

    // 1. Contact info (15 pts)
    let contactScore = 0;
    if (p.email && /^.+@.+\..+$/.test(p.email)) contactScore += 5;
    if (p.phone && p.phone.length >= 7) contactScore += 5;
    if (p.location) contactScore += 5;
    checks.push({
        id: 'contact',
        label: 'Contact info',
        score: contactScore,
        max: 15,
        detail: contactScore === 15
            ? 'Email, phone, and location present.'
            : 'Add missing contact details — recruiters need email + phone.'
    });

    // 2. Professional summary (5 pts)
    const summaryLen = (p.summary || '').trim().length;
    let summaryScore = 0;
    if (summaryLen >= 80 && summaryLen <= 600) summaryScore = 5;
    else if (summaryLen > 0) summaryScore = 2;
    checks.push({
        id: 'summary',
        label: 'Professional summary',
        score: summaryScore,
        max: 5,
        detail: summaryLen === 0
            ? 'Missing — add a 2-3 sentence summary at the top.'
            : summaryScore < 5
                ? `Length ${summaryLen} chars — aim for 80–600.`
                : 'Concise and well-sized.'
    });

    // 3. Experience present (10 pts)
    const expCount = (resumeData.experience || []).length;
    const expScore = expCount === 0 ? 0 : expCount === 1 ? 5 : 10;
    checks.push({
        id: 'experience-present',
        label: 'Work experience entries',
        score: expScore,
        max: 10,
        detail: expCount === 0
            ? 'No experience listed.'
            : `${expCount} role${expCount === 1 ? '' : 's'} listed.`
    });

    // 4. Action verbs (15 pts)
    let verbScore = 0;
    if (allBullets.length > 0) {
        const verbHits = allBullets.filter(startsWithStrongVerb).length;
        const ratio = verbHits / allBullets.length;
        verbScore = Math.round(ratio * 15);
    }
    checks.push({
        id: 'action-verbs',
        label: 'Bullets start with action verbs',
        score: verbScore,
        max: 15,
        detail: allBullets.length === 0
            ? 'No bullets to analyze.'
            : `${Math.round((verbScore / 15) * 100)}% of bullets start with a strong verb (e.g. Spearheaded, Architected).`
    });

    // 5. Quantified achievements (20 pts)
    let metricScore = 0;
    if (allBullets.length > 0) {
        const metricHits = allBullets.filter(containsMetric).length;
        const ratio = metricHits / allBullets.length;
        metricScore = Math.round(ratio * 20);
    }
    checks.push({
        id: 'metrics',
        label: 'Quantified achievements',
        score: metricScore,
        max: 20,
        detail: allBullets.length === 0
            ? 'Add bullets with numbers (e.g. "reduced load time by 40%").'
            : `${Math.round((metricScore / 20) * 100)}% of bullets contain numbers/metrics. Recruiters skim for these.`
    });

    // 6. Education (10 pts)
    const eduCount = (resumeData.education || []).length;
    const eduScore = eduCount > 0 ? 10 : 0;
    checks.push({
        id: 'education',
        label: 'Education section',
        score: eduScore,
        max: 10,
        detail: eduScore === 10 ? `${eduCount} entry/entries.` : 'No education entries.'
    });

    // 7. Skills (10 pts)
    const skillCount = (resumeData.skills || []).length;
    const skillScore = skillCount >= 6 ? 10 : skillCount >= 3 ? 6 : skillCount > 0 ? 3 : 0;
    checks.push({
        id: 'skills',
        label: 'Skills',
        score: skillScore,
        max: 10,
        detail: skillCount === 0
            ? 'No skills listed.'
            : skillCount < 6
                ? `Only ${skillCount} skill${skillCount === 1 ? '' : 's'} — aim for 6+ for keyword density.`
                : `${skillCount} skills listed.`
    });

    // 8. Date completeness (5 pts)
    const exps = resumeData.experience || [];
    const datedExps = exps.filter(e => e.startDate && (e.endDate || e.endDate === 'Present'));
    let dateScore = 0;
    if (exps.length === 0) dateScore = 0;
    else dateScore = Math.round((datedExps.length / exps.length) * 5);
    checks.push({
        id: 'dates',
        label: 'Dates on every role',
        score: dateScore,
        max: 5,
        detail: exps.length === 0
            ? 'No experience to date.'
            : datedExps.length < exps.length
                ? `${exps.length - datedExps.length} role(s) missing dates.`
                : 'All roles dated.'
    });

    // 9. Length sanity (10 pts) — bullets per role
    const avgBulletsPerRole = exps.length ? allBullets.length / exps.length : 0;
    let lengthScore = 0;
    if (exps.length === 0) lengthScore = 0;
    else if (avgBulletsPerRole >= 2 && avgBulletsPerRole <= 6) lengthScore = 10;
    else if (avgBulletsPerRole >= 1) lengthScore = 6;
    else lengthScore = 2;
    checks.push({
        id: 'length',
        label: 'Bullet density',
        score: lengthScore,
        max: 10,
        detail: exps.length === 0
            ? 'No bullets.'
            : avgBulletsPerRole < 2
                ? `Only ${avgBulletsPerRole.toFixed(1)} bullets/role — add detail.`
                : avgBulletsPerRole > 6
                    ? `${avgBulletsPerRole.toFixed(1)} bullets/role — trim to top 3–5 per role.`
                    : 'Healthy 2–6 bullets per role.'
    });

    // 10. Keyword match (only if JD provided)
    let keywordCheck = null;
    if (jobDescription && jobDescription.trim().length > 20) {
        const jdKeywords = extractKeywords(jobDescription);
        const resumeText = [
            p.summary,
            ...(resumeData.skills || []),
            ...exps.flatMap(e => [e.role, e.company, e.description]),
            ...(resumeData.education || []).flatMap(ed => [ed.degree, ed.school])
        ].filter(Boolean).join(' ');
        const resumeKeywords = extractKeywords(resumeText);

        const matched = [...jdKeywords].filter(k => resumeKeywords.has(k));
        const missing = [...jdKeywords].filter(k => !resumeKeywords.has(k));
        const ratio = jdKeywords.size > 0 ? matched.length / jdKeywords.size : 0;
        const kwScore = Math.round(ratio * 100);

        keywordCheck = {
            id: 'keyword-match',
            label: 'JD keyword coverage',
            score: kwScore,
            max: 100,
            matched: matched.slice(0, 20),
            missing: missing.slice(0, 20),
            detail: `${matched.length}/${jdKeywords.size} job-description keywords appear in your resume.`
        };
    }

    const baseScore = checks.reduce((sum, c) => sum + c.score, 0);
    const baseMax = checks.reduce((sum, c) => sum + c.max, 0);

    const percentage = Math.round((baseScore / baseMax) * 100);

    return {
        baseScore,
        baseMax,
        percentage,
        checks,
        keywordCheck,
        grade: getGrade(baseScore / baseMax),
        verdict: getVerdict(percentage, keywordCheck)
    };
};

const getGrade = (ratio) => {
    if (ratio >= 0.9) return { letter: 'A', color: 'emerald', label: 'Excellent' };
    if (ratio >= 0.8) return { letter: 'B', color: 'blue', label: 'Strong' };
    if (ratio >= 0.65) return { letter: 'C', color: 'amber', label: 'Decent' };
    if (ratio >= 0.5) return { letter: 'D', color: 'orange', label: 'Needs work' };
    return { letter: 'F', color: 'red', label: 'Poor' };
};

// Opinionated apply/skip verdict, inspired by career-ops's "don't apply
// below 4.0/5" rule. Combines the structural ATS score with JD keyword
// coverage when a JD is present. If you only have one job-search hour,
// the most valuable thing this tool can do is tell you when NOT to bother.
const getVerdict = (percentage, keywordCheck) => {
    const baseTier = percentage >= 80 ? 'strong' : percentage >= 65 ? 'ok' : 'weak';

    if (!keywordCheck) {
        if (baseTier === 'strong') {
            return {
                action: 'APPLY',
                color: 'emerald',
                headline: 'Ready to send.',
                reasoning: 'Your resume structure scores well across the board. Paste a job description above for a JD-specific apply/skip call.'
            };
        }
        if (baseTier === 'ok') {
            return {
                action: 'BORDERLINE',
                color: 'amber',
                headline: 'Tighten before sending.',
                reasoning: 'A few checks are weak. Fix the items flagged red below — most are 5-minute changes that materially improve recruiter response rate.'
            };
        }
        return {
            action: 'SKIP_REWRITE',
            color: 'red',
            headline: 'Rewrite before applying anywhere.',
            reasoning: 'Multiple structural issues. Address the red items below — sending this as-is is likely to be filtered out by ATS systems before a human reads it.'
        };
    }

    // With JD: combine structural score with keyword coverage
    const kw = keywordCheck.score;
    if (baseTier === 'strong' && kw >= 60) {
        return {
            action: 'APPLY',
            color: 'emerald',
            headline: 'Strong match — apply with confidence.',
            reasoning: `${kw}% JD keyword coverage and a healthy structural score. This resume is well-suited to the role; recruiter throughput should be high.`
        };
    }
    if (baseTier === 'strong' && kw >= 40) {
        return {
            action: 'APPLY_TAILORED',
            color: 'blue',
            headline: 'Apply, but tailor first.',
            reasoning: `Structurally strong but only ${kw}% JD keyword overlap. Add the missing keywords (below) to your summary and bullets where they honestly apply — do not invent skills.`
        };
    }
    if (baseTier === 'ok' && kw >= 50) {
        return {
            action: 'BORDERLINE',
            color: 'amber',
            headline: 'Borderline — fix the basics + tailor.',
            reasoning: `Keyword overlap is reasonable but structural issues will reduce ATS pass-through. Address red items below before sending.`
        };
    }
    if (kw < 30) {
        return {
            action: 'SKIP',
            color: 'red',
            headline: "Don't waste a tailored CV here.",
            reasoning: `Only ${kw}% of the JD's keywords appear in your resume. Either this role is the wrong fit, or your resume is missing experience the role demands. Career-ops's rule: spend your effort on better-matched listings.`
        };
    }
    return {
        action: 'BORDERLINE',
        color: 'amber',
        headline: 'Borderline match.',
        reasoning: 'Mixed signals — addressable with focused edits, but not a slam-dunk.'
    };
};
